'use client';

import { ChangeEvent, InputHTMLAttributes, useEffect, useRef, useState } from 'react';
import cx from 'classnames';

import { useKeyPress } from '@/hooks/useKeyPress';

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
    className?: string;
    containerClassName?: string;
    affix?: string;
    error?: boolean;
    onChange?: (value: string) => void;
    onEnter?: () => void;
}

const Input = ({ className = '', error = false, onChange, onEnter, value = '', affix, ...rest }: InputProps) => {
    const ref = useRef<HTMLInputElement>(null);
    const enterPressed = useKeyPress('Enter');
    const [currentValue, setValue] = useState(value ?? '');

    useEffect(
        () => {
            setValue(value ?? '');
        },
        [value]
    );

    useEffect(
        () => {
            if (ref.current) {
                const hasFocus = document.activeElement === ref.current;
                if (enterPressed && hasFocus && typeof onEnter === 'function') {
                    onEnter();
                }
            }
        },
        [onEnter, enterPressed]
    );

    const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;

        setValue(newValue);

        if (typeof onChange === 'function') {
            onChange(newValue);
        }
    };

    return (
        <div className={cx('w-full flex items-center rounded-lg border overflow-hidden', {
            'border-gray-300': error === false,
            'border-red-500': error === true,
            [className]: Boolean(className)
        })}>
            <input
                ref={ref}
                onChange={handleOnChange}
                value={currentValue}
                className={cx('min-w-0 flex-grow px-4 py-4 border-none font-semibold bg-white outline-none focus:ring-0 transition-colors duration-300', {
                    'placeholder-red-500': error === true
                })}
                type='text'
                {...rest}
            />
            <span className='px-4 cursor-default select-none rounded-md empty:hidden'>
                {affix}
            </span>
        </div>
    );
};

export { Input };